import React from 'react';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportInitiatedTrackingFunction,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { EnabledExportPages, ProductsTabView, ViewType } from '@hooks/export/Model';
import { ExportExcelProps } from '@components/products-line-charts/Types';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { ColumnHeader, ColumnHeaderDisplayName } from '@models/Products';
import { WithExportButton, WithExportModal } from '@components/design';
import ExploreCollectionButton from '@pages/explore/collection-button/ExploreCollectionButton';
import { ProductsViewState } from '@models/SavedViews';
import { PageType, SelectedFilters } from '@utils/pageConfig/Types';
import { ProductsMarketRetailerLineChartInsightParams } from '@data-visualization/products-market-line-chart/Types';

interface ProductTrendsHeaderIconProps extends ExportExcelProps {
  createInsightDataFn: CreateInsightDataFn<ProductsMarketRetailerLineChartInsightParams>;
  productsViewState: ProductsViewState;
  isExportButtonDisabled: boolean;
   onChartDownload: any;
}

const ProductTrendsHeaderIcon: React.FC<ProductTrendsHeaderIconProps> = ({
  onChartDownload,
  isExportButtonDisabled,
  createInsightDataFn,
  productsViewState,
}) => {
  const pagesConfig = usePageConfig();

  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  const getSelectedCountryAndProductGroupString =(selectedFilters: SelectedFilters[]): string =>  {
    const filters = selectedFilters[PageType.RI_PRODUCT]?.selectedFilters ?? [];
  
    const country = filters.find(f => f.dataType === 'COUNTRY')?.options?.[0]?.value ?? '';
    const productGroup = filters.find(f => f.dataType === 'PRODUCT GROUP')?.options?.[0]?.value ?? '';
  
    return [country, productGroup].filter(Boolean).join('_');
  }
 const selectedCountryAndProductGroup = getSelectedCountryAndProductGroupString(pagesConfig.appliedFilters);
  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.Products,
    currentTab: ProductsTabView.Trends,
    currentView: ViewType.LineChart,
    currentCountryAndProduct: selectedCountryAndProductGroup,
  });

  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersProducts', 'hitlistsRetailerClient');

  const handleExportTracking = () => {
    handleDataExportTrackingFunction(
      {
        elementType: ElementType.LINE_CHART,
        elementKpi: ColumnHeaderDisplayName[pagesConfig.sortingOptions.headerKey as ColumnHeader],
        elementSort: pagesConfig.sortingOptions,
      },
      dataExportInitiatedTrackingFunction,
    );
  };

  return (
    <>
      <ExploreCollectionButton
        isDisabled={isExportButtonDisabled}
        createInsightDataFn={createInsightDataFn}
        exploreViewForCollections={'Products Line Chart'}
        createViewStateFn={() => productsViewState}
      />
      <WithExportButton disabled={isExportButtonDisabled} onClick={onExportClick} data-test-id="export-chart-btn" />
      <WithExportModal
        isShown={isExportModalOpen}
        exportFormats={exportModalFormats}
        message={exportModalMessage}
        title={exportModalHeaderText}
        download={onChartDownload}
        onClose={onCloseExportModal}
        onError={onExportError}
        data-test-id="export-modal"
      />
    </>
  );
};

export default ProductTrendsHeaderIcon;
